import { useState, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';
import { images } from '../../assets';

const Footer = () => {
  const [hasScrolledPastTop, setHasScrolledPastTop] = useState(false);

  const { ref, inView } = useInView({
    triggerOnce: false,
    threshold: 0.05,
    initialInView: false,
  });

  useEffect(() => {
    const handleScroll = () => {
      // Must have scrolled well past the hero section before curtain reveal can become active
      if (window.scrollY > 400) {
        setHasScrolledPastTop(true);
      } else {
        setHasScrolledPastTop(false);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isCurtainActive = inView && hasScrolledPastTop;

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  return (
    <>
      <footer id="contacto" className="bg-black text-white pt-14 pb-4 px-4 sm:px-6 lg:px-8 relative z-10 border-t border-neutral-900">
        <div className="max-w-7xl mx-auto">
          {/* Top CTA Banner (Trae Style) */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 pb-12 border-b border-neutral-800/80">
            <div>
              <h2 className="anton-font text-3xl sm:text-4xl md:text-5xl text-white tracking-wide uppercase leading-tight">
                Empieza en Energy
              </h2>
              <p className="text-neutral-400 text-base sm:text-lg font-medium mt-1">
                Entrena con resultados reales hoy mismo
              </p>
            </div>
            <div className="flex items-center">
              <Link
                to="/descargar-app"
                className="inline-flex items-center justify-center gap-3 bg-blue-600 hover:bg-blue-800 text-white px-6 py-3.5 sm:px-8 sm:py-4 transition-colors font-sans font-medium text-base md:text-lg shadow-lg tracking-wider"
              >
                <span>Comienza a entrenar</span>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </Link>
            </div>
          </div>

          {/* Multi-Column Links Section */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-8 lg:gap-6 py-10 border-b border-neutral-800/80 text-left">
            {/* Col 1: Brand Info */}
            <div className="lg:col-span-4 flex flex-col justify-between space-y-4">
              <div className="flex items-center space-x-3.5">
                <img src={images.logo_gym} alt="Logo de Energy" className="h-14 sm:h-16 w-auto opacity-95" />
                <div className="flex flex-col">
                  <span
                    className="anton-font text-3xl sm:text-4xl md:text-5xl text-white leading-none"
                    style={{ letterSpacing: '4px' }}
                  >
                    ENERGY
                  </span>
                  <span className="text-[11px] sm:text-xs text-neutral-400 uppercase tracking-widest font-semibold mt-1">
                    Gimnasio Huánuco
                  </span>
                </div>
              </div>
              {/* <div>
                <span className="inline-flex items-center gap-1.5 text-xs text-neutral-300">
                  <span className="w-2 h-2 rounded-full bg-[#00ff87] animate-pulse"></span>
                  Abierto Lun - Sáb
                </span>
              </div> */}
            </div>

            {/* Col 2: Navegación */}
            <div className="lg:col-span-2">
              <h3 className="text-white text-xs font-semibold uppercase tracking-wider mb-3.5">Navegación</h3>
              <ul className="space-y-2 text-xs sm:text-sm text-neutral-400">
                <li><a href="#inicio" className="hover:text-white transition-colors">Inicio</a></li>
                <li><a href="#nosotros" className="hover:text-white transition-colors">Sobre Nosotros</a></li>
                <li><a href="#tarifa" className="hover:text-white transition-colors">Horarios y Tarifas</a></li>
                <li><a href="#resultados" className="hover:text-white transition-colors">Resultados</a></li>
                <li><a href="#preguntas-frecuentes" className="hover:text-white transition-colors">Preguntas Frecuentes</a></li>
              </ul>
            </div>

            {/* Col 3: Contacto */}
            <div className="lg:col-span-3">
              <h3 className="text-white text-xs font-semibold uppercase tracking-wider mb-3.5">Contacto</h3>
              <div className="space-y-3 text-xs sm:text-sm text-neutral-400">
                <div>
                  <a href="mailto:contacto@gimnasioenergy.com" className="hover:text-cyan-400 transition-colors block font-medium">
                    contacto@gimnasioenergy.com
                  </a>
                  <a href="mailto:energyhuanuco@gmail.com" className="hover:text-cyan-400 transition-colors block font-medium">
                    energyhuanuco@gmail.com
                  </a>
                </div>
                <div className="pt-1">
                  <h4 className="text-white text-xs font-semibold uppercase tracking-wider mb-1">Ubicación</h4>
                  <a
                    href="https://maps.app.goo.gl/3kVH4nAgpWf4LsNLA"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-cyan-400 transition-colors block"
                  >
                    Jr. Aguilar 771 - Piso 2, Huánuco
                  </a>
                </div>
              </div>
            </div>

            {/* Col 4: Legal & Mini Mapa */}
            <div className="lg:col-span-3 flex flex-col justify-between space-y-4">
              <div>
                <h3 className="text-white text-xs font-semibold uppercase tracking-wider mb-3.5">Legal</h3>
                <ul className="space-y-2 text-xs sm:text-sm text-neutral-400">
                  <li>
                    <Link to="/terminos-y-condiciones" className="hover:text-white transition-colors">
                      Términos de Servicio
                    </Link>
                  </li>
                  <li>
                    <Link to="/politica-de-privacidad" className="hover:text-white transition-colors">
                      Política de Privacidad
                    </Link>
                  </li>
                </ul>
              </div>

              {/* Mini mapa compacto */}
              <div className="rounded-lg overflow-hidden border border-neutral-800 bg-neutral-900 group">
                <iframe
                  title="Ubicación Gimnasio Energy Huánuco"
                  src="https://maps.google.com/maps?q=-9.9332365,-76.2406938&hl=es&z=17&output=embed"
                  className="w-full h-20 border-0 filter contrast-[105%] group-hover:contrast-[115%] transition-all"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                ></iframe>
                <a
                  href="https://maps.app.goo.gl/3kVH4nAgpWf4LsNLA"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block bg-neutral-950 px-2.5 py-1 text-[11px] text-center text-neutral-400 hover:text-cyan-400 border-t border-neutral-800 transition-colors font-medium"
                >
                  Ver en Google Maps &rarr;
                </a>
              </div>
            </div>
          </div>

          {/* Bottom Bar (Trae Style) */}
          <div className="py-5 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-neutral-400">
            <div className="flex items-center gap-2">
              <span>© {new Date().getFullYear()} GIMNASIO ENERGY</span>
              <span className="text-neutral-600">•</span>
              <span className="text-neutral-500">Huánuco, Perú</span>
            </div>

            {/* Social Media Icons (compact & elegant) */}
            <div className="flex items-center space-x-3">
              <a
                href="https://www.tiktok.com/@energyhco"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="TikTok"
                className="opacity-75 hover:opacity-100 transform hover:scale-110 transition-all p-1.5 rounded-lg hover:bg-neutral-900"
              >
                <img src={images.tik_tok} alt="TikTok" className="h-5 w-5 object-contain" />
              </a>
              <a
                href="https://www.facebook.com/energy.huanuco"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
                className="opacity-75 hover:opacity-100 transform hover:scale-110 transition-all p-1.5 rounded-lg hover:bg-neutral-900"
              >
                <img src={images.facebook} alt="Facebook" className="h-5 w-5 object-contain" />
              </a>
              <a
                href="https://www.instagram.com/energyhuanuco/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                className="opacity-75 hover:opacity-100 transform hover:scale-110 transition-all p-1.5 rounded-lg hover:bg-neutral-900"
              >
                <img src={images.instagram} alt="Instagram" className="h-5 w-5 object-contain" />
              </a>
            </div>

            {/* Back to top */}
            <div>
              <button
                type="button"
                onClick={scrollToTop}
                className="inline-flex items-center gap-1.5 text-neutral-400 hover:text-white transition-colors cursor-pointer group"
              >
                <span>Volver arriba</span>
                <svg className="w-3.5 h-3.5 transform group-hover:-translate-y-0.5 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </footer>

      {/* Curtain Reveal Brand Area */}
      <div ref={ref} className="relative w-full overflow-hidden">
        {/* Fixed element that sits behind everything */}
        <div
          className={`fixed bottom-0 left-0 w-full bg-blue-700 flex items-center justify-center pt-2 sm:pt-3 md:pt-4 pb-0 px-1 sm:px-2 md:px-4 transition-opacity duration-300 ${
            isCurtainActive ? 'opacity-100' : 'opacity-0 pointer-events-none invisible'
          }`}
          style={{ zIndex: -10 }}
        >
          <div className="w-full flex justify-between items-center">
            {['E', 'N', 'E', 'R', 'G', 'Y'].map((letter, index) => (
              <span key={index} className="bebas-font text-black text-[22vw] sm:text-[23vw] md:text-[24vw] lg:text-[25vw] leading-[0.78] select-none text-center">
                {letter}
              </span>
            ))}
          </div>
        </div>

        {/* Invisible spacer to create exact scrollable height in the document */}
        <div className="w-full flex items-center justify-center pt-2 sm:pt-3 md:pt-4 pb-0 px-1 sm:px-2 md:px-4 opacity-0 pointer-events-none">
          <div className="w-full flex justify-between items-center">
            {['E', 'N', 'E', 'R', 'G', 'Y'].map((letter, index) => (
              <span key={index} className="bebas-font text-[22vw] sm:text-[23vw] md:text-[24vw] lg:text-[26vw] leading-[0.78] text-center">
                {letter}
              </span>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default Footer;
